import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios'
import type { RequestError } from '@/modules/shared/domain/requestError'

const baseURL = import.meta.env.VITE_API_URL || '/api'

export const http = axios.create({
  baseURL,
})

export const get = <T>(
  url: string,
  params: Record<string, unknown> = {},
): Promise<AxiosResponse<T>> => {
  return http.get<T>(url, { params })
}

/**
 * A binary GET (a PDF, an export) through the SAME instance as everything
 * else, so it inherits the auth header, the 401 refresh-and-retry and the 402
 * gate. A bare `axios.get` here would download fine while logged in and 401
 * forever the moment the access token turned over.
 *
 * The caller gets the whole response, not just the body: the file NAME lives
 * in `Content-Disposition`, and dropping the headers would throw it away.
 */
export const getBlob = (
  url: string,
  params: Record<string, unknown> = {},
): Promise<AxiosResponse<Blob>> => {
  return http.get<Blob>(url, { params, responseType: 'blob' })
}

// `FormData` is accepted alongside a plain object so multipart uploads go
// through the same instance — and therefore the same auth, refresh and 402
// interceptors. Axios sets the multipart boundary itself; setting
// Content-Type by hand here would produce a boundary-less header the server
// cannot parse.
export const post = <T>(
  url: string,
  data: Record<string, unknown> | FormData = {},
  config: AxiosRequestConfig = {},
): Promise<AxiosResponse<T>> => {
  return http.post<T>(url, data, config)
}

export const put = <T>(
  url: string,
  data: Record<string, unknown> = {},
): Promise<AxiosResponse<T>> => {
  return http.put<T>(url, data)
}

export const del = <T>(
  url: string,
  data: Record<string, unknown> = {},
): Promise<AxiosResponse<T>> => {
  return http.delete<T>(url, { data })
}

/**
 * The single place a thrown request turns into a `RequestError`. Callers that
 * need the response itself (headers, not just the body) catch on their own and
 * come here, so every failure in the app still carries the same shape.
 */
export const toRequestError = (error: unknown): RequestError => ({
  name: 'RequestError',
  status: axios.isAxiosError(error) ? error.response?.status : undefined,
  message: error instanceof Error ? error.message : String(error),
  data: axios.isAxiosError(error) ? error.response?.data : undefined,
})

/**
 * A refusal the server took the trouble to EXPLAIN.
 *
 * Features answer a rejected command with `BadRequest(result)` — a 4xx whose
 * body still carries the specific message code (`team.emailTaken`, and dozens
 * like it). Axios throws on 4xx, so that body used to be dropped on the floor
 * and every screen printed its generic fallback instead: the API said "there
 * is already an account with that email" and the person read "the employee
 * could not be registered".
 *
 * Only a well-formed refusal envelope is handed back. A ProblemDetails, an
 * HTML error page or a failed GET returns nothing, exactly as before.
 */
const refusalEnvelope = <T>(error: RequestError): T | undefined => {
  const body = error.data
  return typeof body === 'object' && body !== null && 'success' in body
    && (body as { success: unknown }).success === false
    ? (body as T)
    : undefined
}

export const catchError = async <T>(
  promise: Promise<AxiosResponse<T>>,
): Promise<[RequestError | undefined, T | undefined]> => {
  try {
    const response = await promise
    return [undefined, response.data]
  } catch (error) {
    // The error stays set, so every `if (error || !result)` guard refuses
    // exactly as it did before. The body rides along only so the screen can
    // say WHICH refusal it was.
    const requestError = toRequestError(error)
    return [requestError, refusalEnvelope<T>(requestError)]
  }
}

http.interceptors.request.use(async (config) => {
  const { useAuthStore } = await import('@/modules/auth/store/auth')
  const token = useAuthStore().token
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// A request config that survived one retry after a token refresh. Marked
// so a SECOND 401 on the same request falls straight through to logout
// instead of refreshing (and replaying) forever.
interface RetryableRequestConfig extends AxiosRequestConfig {
  _retry?: boolean
}

interface RefreshTokenResponse {
  success: boolean
  message: string
  accessToken: string | null
  newRefreshToken: string | null
}

// Several requests can 401 around the same moment (a page firing parallel
// calls). They must share ONE in-flight refresh: the server rotates the
// refresh token on every use, so a second concurrent refresh would spend
// a token the first refresh already consumed and fail everyone but the
// first waiter. Reset to null once settled so a LATER, unrelated expiry
// starts a fresh refresh instead of reusing a stale completed promise.
let inFlightRefresh: Promise<string | null> | null = null

// Issues POST /web/auth/refresh-token via the bare `axios` export, never the
// `http` instance — this is what keeps a refresh failure from recursing
// back into this very interceptor (a 401 from the refresh call itself
// never reaches `http`'s response interceptor at all).
async function refreshAccessToken(): Promise<string | null> {
  const { useAuthStore } = await import('@/modules/auth/store/auth')
  const authStore = useAuthStore()
  const storedRefreshToken = authStore.refreshToken

  if (!storedRefreshToken) {
    return null
  }

  try {
    const response = await axios.post<RefreshTokenResponse>(`${baseURL}/web/auth/refresh-token`, {
      refreshToken: storedRefreshToken,
    })

    // A 200 with success:false (or null tokens) is NOT a thrown error —
    // it must be treated as a failed refresh, not replayed with a null token.
    const { success, accessToken, newRefreshToken } = response.data
    if (!success || !accessToken || !newRefreshToken) {
      return null
    }

    // The old refresh token was just consumed server-side (rotation): persist
    // BOTH tokens, or the next refresh attempt spends an already-dead one.
    authStore.setTokens(accessToken, newRefreshToken)
    return accessToken
  } catch {
    return null
  }
}

function getRefreshedAccessToken(): Promise<string | null> {
  if (!inFlightRefresh) {
    inFlightRefresh = refreshAccessToken().finally(() => {
      inFlightRefresh = null
    })
  }
  return inFlightRefresh
}

/**
 * Why the session ended, carried from the 401 to the login screen.
 *
 * A browser that is simply dumped at the login screen reads as a crash. The
 * server names the reason as a CODE — `auth.sessionSignedInElsewhere` and its
 * siblings — and the login page turns it into words, so "somebody signed in
 * from another browser" is something the product SAYS rather than something
 * the user has to infer.
 */
const SESSION_END_REASON_KEY = 'session_end_reason'

export const takeSessionEndReason = (): string | null => {
  const reason = sessionStorage.getItem(SESSION_END_REASON_KEY)
  // Read once: the explanation belongs to the sign-out that just happened, not
  // to every future visit to the login screen.
  sessionStorage.removeItem(SESSION_END_REASON_KEY)
  return reason
}

async function forceLogout(reason?: string): Promise<void> {
  if (reason) {
    sessionStorage.setItem(SESSION_END_REASON_KEY, reason)
  }

  const { useAuthStore } = await import('@/modules/auth/store/auth')
  useAuthStore().clearSession()
  // Clear subscription state too, or forcedReadOnly survives the forced logout and
  // leaks into the next session on a shared browser.
  const { useSubscriptionStore } = await import('@/modules/subscription/store/subscription')
  useSubscriptionStore().reset()
  const { default: router } = await import('@/router')
  if (router.currentRoute.value.name !== 'login') {
    await router.push({ name: 'login' })
  }
}

http.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error?.response?.status === 401) {
      const originalRequest = error?.config as RetryableRequestConfig | undefined

      if (originalRequest && !originalRequest._retry) {
        originalRequest._retry = true
        const newAccessToken = await getRefreshedAccessToken()
        if (newAccessToken) {
          // Re-enters the request interceptor, which re-reads the (now
          // refreshed) token from the store — no manual header patching.
          return http(originalRequest)
        }
      }

      // The web session gate answers with a code saying WHICH ending this was.
      const code = error?.response?.data?.code
      await forceLogout(typeof code === 'string' ? code : undefined)
      return Promise.reject(error)
    }

    // 402 = the read-only gate refused a write (subscription lapsed). Reflect
    // it in the UI at once instead of failing silently.
    if (error?.response?.status === 402) {
      const { useSubscriptionStore } = await import('@/modules/subscription/store/subscription')
      useSubscriptionStore().markReadOnly()
    }

    return Promise.reject(error)
  },
)
